import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Plus, X, Save } from 'lucide-react';
import { Button } from '../components/common';

const EMPTY_INGREDIENT = { qty: '', unit: '', item: '', group: 'Main' };
const EMPTY_STEP = { order: 1, instruction: '', timerMinutes: '' };

const Label = ({ children }) => (
  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-night-muted mb-1.5">
    {children}
  </label>
);

const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-night-card rounded-lg bg-white dark:bg-night-card text-sm text-gray-900 dark:text-night-text placeholder-gray-400 dark:placeholder-night-muted focus:outline-none focus:ring-2 focus:ring-primary-500';
const textareaClass = `${inputClass} resize-y`;

export default function RecipeEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { recipes, addRecipe, updateRecipe, categories } = useApp();
  const isEditing = !!id;

  const [form, setForm] = useState({
    title: '', description: '', sourceUrl: '', servings: 4,
    prepTime: '', cookTime: '', totalTime: '',
    categories: [], tags: [],
    ingredients: [{ ...EMPTY_INGREDIENT }],
    steps: [{ ...EMPTY_STEP }],
    notes: '', favorite: false,
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const recipe = recipes.find((r) => r.id === parseInt(id, 10));
      if (recipe) {
        setForm({
          ...recipe,
          prepTime: recipe.prepTime || '',
          cookTime: recipe.cookTime || '',
          totalTime: recipe.totalTime || '',
          ingredients: recipe.ingredients?.length ? recipe.ingredients : [{ ...EMPTY_INGREDIENT }],
          steps: recipe.steps?.length ? recipe.steps : [{ ...EMPTY_STEP }],
          tags: recipe.tags || [],
          categories: recipe.categories || [],
        });
      }
    } else if (location.state?.prefill) {
      setForm((f) => ({ ...f, ...location.state.prefill }));
    }
  }, [id, isEditing, recipes, location.state]);

  const updateField = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const updateIngredient = (i, field, value) =>
    setForm((p) => {
      const ing = [...p.ingredients];
      ing[i] = { ...ing[i], [field]: value };
      return { ...p, ingredients: ing };
    });

  const addIngredient = () =>
    setForm((p) => ({ ...p, ingredients: [...p.ingredients, { ...EMPTY_INGREDIENT }] }));

  const removeIngredient = (i) =>
    setForm((p) => ({ ...p, ingredients: p.ingredients.filter((_, idx) => idx !== i) }));

  const updateStep = (i, field, value) =>
    setForm((p) => {
      const steps = [...p.steps];
      steps[i] = { ...steps[i], [field]: value };
      return { ...p, steps };
    });

  const addStep = () =>
    setForm((p) => ({
      ...p,
      steps: [...p.steps, { order: p.steps.length + 1, instruction: '', timerMinutes: '' }],
    }));

  const removeStep = (i) =>
    setForm((p) => ({
      ...p,
      steps: p.steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, order: idx + 1 })),
    }));

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      updateField('tags', [...form.tags, t]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => updateField('tags', form.tags.filter((t) => t !== tag));

  const toggleCategory = (cat) => {
    if (form.categories.includes(cat)) updateField('categories', form.categories.filter((c) => c !== cat));
    else updateField('categories', [...form.categories, cat]);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const recipeData = {
      ...form,
      servings: parseInt(form.servings, 10) || 4,
      prepTime: parseInt(form.prepTime, 10) || 0,
      cookTime: parseInt(form.cookTime, 10) || 0,
      totalTime:
        parseInt(form.totalTime, 10) ||
        (parseInt(form.prepTime, 10) || 0) + (parseInt(form.cookTime, 10) || 0),
      ingredients: form.ingredients
        .filter((i) => i.item.trim())
        .map((i) => ({ ...i, qty: parseFloat(i.qty) || 0 })),
      steps: form.steps
        .filter((s) => s.instruction.trim())
        .map((s, i) => ({ ...s, order: i + 1, timerMinutes: parseInt(s.timerMinutes, 10) || null })),
    };
    if (isEditing) {
      await updateRecipe(parseInt(id, 10), recipeData);
      navigate(`/recipes/${id}`);
    } else {
      const newId = await addRecipe(recipeData);
      navigate(`/recipes/${newId}`);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <Label>Recipe Title *</Label>
        <input type="text" className={inputClass} placeholder="What are you cooking?" value={form.title} onChange={(e) => updateField('title', e.target.value)} />
      </div>

      <div>
        <Label>Description</Label>
        <textarea className={textareaClass} rows={3} placeholder="Briefly describe this recipe…" value={form.description} onChange={(e) => updateField('description', e.target.value)} />
      </div>

      <div>
        <Label>Source URL</Label>
        <input type="url" className={inputClass} placeholder="https://…" value={form.sourceUrl} onChange={(e) => updateField('sourceUrl', e.target.value)} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Servings</Label>
          <input type="number" min="1" className={inputClass} value={form.servings} onChange={(e) => updateField('servings', e.target.value)} />
        </div>
        <div>
          <Label>Prep (min)</Label>
          <input type="number" min="0" className={inputClass} value={form.prepTime} onChange={(e) => updateField('prepTime', e.target.value)} />
        </div>
        <div>
          <Label>Cook (min)</Label>
          <input type="number" min="0" className={inputClass} value={form.cookTime} onChange={(e) => updateField('cookTime', e.target.value)} />
        </div>
      </div>

      <div>
        <Label>Categories</Label>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => {
            const active = form.categories.includes(cat.name);
            return (
              <button
                key={cat.id}
                type="button"
                aria-pressed={active}
                onClick={() => toggleCategory(cat.name)}
                className={[
                  'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
                  active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white dark:bg-night-surface border-gray-200 dark:border-night-card text-gray-700 dark:text-night-text',
                ].join(' ')}
              >
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label>Tags</Label>
        <div className="flex gap-2">
          <input type="text" className={inputClass} placeholder="Add a tag…" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
          <Button onClick={addTag} aria-label="Add tag" className="min-h-0 py-2 px-3">
            <Plus size={16} />
          </Button>
        </div>
        {form.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {form.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-night-card text-xs text-gray-700 dark:text-night-text">
                {tag}
                <button onClick={() => removeTag(tag)} aria-label={`Remove ${tag}`} className="text-gray-400 hover:text-gray-600">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label>Ingredients</Label>
        <div className="space-y-2">
          {form.ingredients.map((ing, i) => (
            <div key={i} className="grid grid-cols-[60px_72px_1fr_auto] gap-2 items-start">
              <input type="text" className={inputClass} placeholder="Qty" value={ing.qty} onChange={(e) => updateIngredient(i, 'qty', e.target.value)} />
              <input type="text" className={inputClass} placeholder="Unit" value={ing.unit} onChange={(e) => updateIngredient(i, 'unit', e.target.value)} />
              <input type="text" className={inputClass} placeholder="Ingredient name" value={ing.item} onChange={(e) => updateIngredient(i, 'item', e.target.value)} />
              <button onClick={() => removeIngredient(i)} aria-label="Remove ingredient" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-400 dark:text-night-muted">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={addIngredient} className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline">
          <Plus size={16} aria-hidden="true" /> Add Ingredient
        </button>
      </div>

      <div>
        <Label>Instructions</Label>
        <div className="space-y-3">
          {form.steps.map((step, i) => (
            <div key={i} className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-semibold flex items-center justify-center mt-1">
                {i + 1}
              </div>
              <div className="flex-1 space-y-2">
                <textarea className={textareaClass} rows={2} placeholder={`Step ${i + 1}…`} value={step.instruction} onChange={(e) => updateStep(i, 'instruction', e.target.value)} />
                <input type="number" min="0" className={inputClass} placeholder="Timer (min, optional)" value={step.timerMinutes || ''} onChange={(e) => updateStep(i, 'timerMinutes', e.target.value)} />
              </div>
              <button onClick={() => removeStep(i)} aria-label="Remove step" className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-400 dark:text-night-muted">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <button onClick={addStep} className="mt-2 inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline">
          <Plus size={16} aria-hidden="true" /> Add Step
        </button>
      </div>

      <div>
        <Label>Personal Notes</Label>
        <textarea className={textareaClass} rows={3} placeholder="Tips, modifications, memories about this recipe…" value={form.notes} onChange={(e) => updateField('notes', e.target.value)} />
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        <Button onClick={handleSave} disabled={!form.title.trim() || saving} className="flex-1">
          <Save size={18} aria-hidden="true" />
          {saving ? 'Saving…' : isEditing ? 'Update Recipe' : 'Save Recipe'}
        </Button>
      </div>
    </div>
  );
}
